/**
 * Discovery Mode: AI co-pilot that helps customers map their product.
 * Shows a side panel with live notes, questions, and coverage tracking.
 */

// Web Speech API type declarations (not in all TS lib configs)
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: Event) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

import { SPADetector } from '../capture/spa-detector.js';
import { capturePageSnapshot, captureOpenModals, type PageSnapshot, type ModalSnapshot } from '../capture/element-mapper.js';
import { sanitizeSnapshot } from '../capture/sanitizer.js';
import { generateSelector } from '../capture/selector-gen.js';

export interface DiscoveryConfig {
  apiUrl: string;
  apiKey: string;
  sessionId: string;
  token: string;
  voiceEnabled?: boolean;
}

export interface ClickEvent {
  selector: string;
  label: string;
  tag: string;
  urlPath: string;
  timestamp: number;
}

// Hide TTS + STT from end users while voice work is paused.
// Flip back to true to restore the speaker / mic UI.
const VOICE_ENABLED = false;

export class DiscoveryMode {
  private config: DiscoveryConfig;
  private spaDetector: SPADetector;
  private panel: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private messagesEl: HTMLDivElement | null = null;
  private coverageEl: HTMLDivElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private pagesDiscovered = 0;
  private elementsDiscovered = 0;
  private discoveredPaths = new Set<string>();
  private clickSequence: ClickEvent[] = [];
  private modalObserver: MutationObserver | null = null;
  private lastContentHash = '';
  private contentChangeTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollObserver: IntersectionObserver | null = null;
  private observedSentinels: Set<Element> = new Set();

  // Voice state
  private voiceEnabled = false;
  private ttsEnabled = false;
  private isListening = false;
  private isSpeaking = false;
  private micToggled = false;
  private recognition: SpeechRecognition | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private micBtnEl: HTMLButtonElement | null = null;
  private speakerBtnEl: HTMLButtonElement | null = null;

  // Click sync state
  private lastSyncedClickIndex = 0;
  private clickSyncTimer: ReturnType<typeof setInterval> | null = null;
  private beforeUnloadHandler: (() => void) | null = null;

  constructor(config: DiscoveryConfig) {
    this.config = config;
    this.spaDetector = new SPADetector((info) => this.onRouteChange(info.url, info.title));
  }

  async start(): Promise<void> {
    this.renderPanel();
    this.spaDetector.start();
    this.startClickRecording();
    this.startModalDetection();
    this.startContentChangeDetection();
    this.startScrollDetection();
    this.startClickSync();
    this.initVoice();

    // Capture initial page
    await this.captureCurrentPage();

    // Greet
    this.addAgentMessage(
      "Hey! I'm here to learn about your product so I can set up an interactive demo for your visitors.\n\nLet's walk through it together — tell me about what we're looking at?",
    );
  }

  stop(): void {
    this.spaDetector.stop();
    this.stopClickRecording();
    this.stopClickSync();
    this.stopVoice();
    this.modalObserver?.disconnect();
    this.modalObserver = null;
    this.scrollObserver?.disconnect();
    this.scrollObserver = null;
    this.observedSentinels.clear();
    if (this.contentChangeTimer) {
      clearTimeout(this.contentChangeTimer);
      this.contentChangeTimer = null;
    }
    this.panel?.remove();
  }

  // ─── Click Sequence Recording ──────────────────────────────────────

  private clickHandler = (e: MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (!target || this.panel?.contains(target)) return;

    // Only record interactive elements
    const interactive = target.closest('button, a, [role="button"], [role="tab"], [role="menuitem"], input, select, textarea, [data-testid], [data-demo-id]');
    if (!interactive) return;

    // Prevent new-tab navigation — keep everything in the current page for discovery
    const anchor = interactive.closest('a') as HTMLAnchorElement | null;
    if (anchor && anchor.target === '_blank') {
      anchor.target = '_self';
      requestAnimationFrame(() => { anchor.target = '_blank'; });
    }

    const label =
      interactive.getAttribute('aria-label') ||
      interactive.getAttribute('title') ||
      (interactive as HTMLElement).innerText?.trim().slice(0, 60) ||
      '';

    if (!label) return;

    this.clickSequence.push({
      selector: generateSelector(interactive),
      label,
      tag: interactive.tagName.toLowerCase(),
      urlPath: location.pathname,
      timestamp: Date.now(),
    });

    // Cap at 500 click events
    if (this.clickSequence.length > 500) {
      this.clickSequence = this.clickSequence.slice(-500);
    }

    // Check if the click caused a content change (tab switch, accordion, etc.)
    this.scheduleContentChangeCheck();
  };

  private startClickRecording(): void {
    document.addEventListener('click', this.clickHandler, true);
  }

  private stopClickRecording(): void {
    document.removeEventListener('click', this.clickHandler, true);
  }

  // ─── Modal/Dialog Detection ────────────────────────────────────────

  private startModalDetection(): void {
    this.modalObserver = new MutationObserver(() => {
      // Debounce modal checks — modals often animate in
      clearTimeout(this.modalDetectTimer);
      this.modalDetectTimer = setTimeout(() => this.checkForModals(), 300);
    });

    this.modalObserver.observe(document.body, {
      childList: true,
      subtree: false,  // Only direct children of body (where modals usually append)
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'open'],
    });
  }

  private modalDetectTimer: ReturnType<typeof setTimeout> | undefined;

  private checkForModals(): void {
    const modals = captureOpenModals();
    if (modals.length === 0) return;

    for (const modal of modals) {
      const modalKey = `modal:${modal.title}`;
      if (this.discoveredPaths.has(modalKey)) continue;
      this.discoveredPaths.add(modalKey);

      this.elementsDiscovered += modal.elements.length;
      this.updateCoverage();
      this.addSystemMessage(`🔲 Modal detected: ${modal.title} (${modal.elements.length} elements)`);

      // Send modal snapshot to API
      this.sendModalSnapshot(modal);
    }
  }

  private async sendModalSnapshot(modal: ModalSnapshot): Promise<void> {
    try {
      await fetch(`${this.config.apiUrl}/api/v1/discover/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          snapshot: {
            urlPath: `${location.pathname}#modal:${modal.title}`,
            pageTitle: `Modal: ${modal.title}`,
            headings: [],
            elements: modal.elements,
            navigation: [],
            forms: modal.forms,
            tables: [],
            viewport: { width: window.innerWidth, height: window.innerHeight },
          },
        }),
      });
    } catch {
      // Non-fatal
    }
  }

  // ─── In-Page State Change Detection ────────────────────────────────

  private startContentChangeDetection(): void {
    // Snapshot the current content fingerprint
    this.lastContentHash = this.getContentFingerprint();
  }

  private getContentFingerprint(): string {
    // Quick fingerprint based on visible headings and main content area
    const main = document.querySelector('main, [role="main"], #app, #root, .app') ?? document.body;
    const headings = Array.from(main.querySelectorAll('h1, h2, h3'))
      .map((h) => (h as HTMLElement).innerText?.trim())
      .filter(Boolean)
      .join('|');
    const tabActive = document.querySelector('[role="tab"][aria-selected="true"]')?.textContent?.trim() ?? '';
    return `${headings}::${tabActive}`;
  }

  /** Called periodically (e.g., after click events) to detect in-page content changes */
  private scheduleContentChangeCheck(): void {
    if (this.contentChangeTimer) return;
    this.contentChangeTimer = setTimeout(() => {
      this.contentChangeTimer = null;
      const newHash = this.getContentFingerprint();
      if (newHash !== this.lastContentHash && newHash.length > 5) {
        this.lastContentHash = newHash;
        this.onContentChange();
      }
    }, 800); // Wait for DOM to settle after interaction
  }

  private async onContentChange(): Promise<void> {
    // Capture the changed content as a sub-page snapshot
    const snapshot = capturePageSnapshot();
    const sanitized = sanitizeSnapshot(snapshot);

    // Use content hash to check if we've seen this state
    const stateKey = `${sanitized.urlPath}::${sanitized.headings.join('|')}`;
    if (this.discoveredPaths.has(stateKey)) return;
    this.discoveredPaths.add(stateKey);

    this.elementsDiscovered += sanitized.elements.length;
    this.updateCoverage();
    this.addSystemMessage(`🔄 In-page change detected — re-captured ${sanitized.urlPath}`);

    try {
      await fetch(`${this.config.apiUrl}/api/v1/discover/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          snapshot: sanitized,
        }),
      });
    } catch {
      // Non-fatal
    }
  }

  private async onRouteChange(_url: string, _title: string): Promise<void> {
    // Small delay for DOM to render after route change
    await new Promise((r) => setTimeout(r, 500));
    await this.captureCurrentPage();
  }

  private async captureCurrentPage(): Promise<void> {
    const snapshot = capturePageSnapshot();
    const sanitized = sanitizeSnapshot(snapshot);

    // Check if we already captured this path
    if (this.discoveredPaths.has(sanitized.urlPath)) {
      this.addSystemMessage(`Revisiting ${sanitized.urlPath} - already mapped.`);
      return;
    }

    this.discoveredPaths.add(sanitized.urlPath);
    this.pagesDiscovered++;
    this.elementsDiscovered += sanitized.elements.length;
    this.updateCoverage();

    // Show "analyzing" indicator
    this.addSystemMessage(`📍 Analyzing: ${sanitized.pageTitle || sanitized.urlPath}...`);

    // Refresh scroll sentinels for the new page
    this.refreshScrollSentinels();

    // Send snapshot to API
    try {
      await fetch(`${this.config.apiUrl}/api/v1/discover/snapshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          snapshot: sanitized,
        }),
      });
    } catch {
      // Non-fatal
    }

    // Get AI analysis
    await this.getAgentAnalysis(sanitized);
  }

  private async getAgentAnalysis(snapshot: PageSnapshot): Promise<void> {
    try {
      const snapshotSummary = {
        url: snapshot.urlPath,
        title: snapshot.pageTitle,
        headings: snapshot.headings,
        elementCount: snapshot.elements.length,
        elementTypes: snapshot.elements.map((e) => `${e.type}: ${e.label}`).slice(0, 20),
        navigation: snapshot.navigation.map((n) => n.text).slice(0, 10),
        forms: snapshot.forms.map((f) => `${f.id}: ${f.fields.length} fields`),
        tables: snapshot.tables.map((t) => `${t.headers.join(', ')} (${t.rowCount} rows)`),
      };

      const message = `[SYSTEM: New page captured]\nURL: ${snapshot.urlPath}\nTitle: ${snapshot.pageTitle}\nHeadings: ${snapshot.headings.join(', ')}\nElements: ${snapshot.elements.length} interactive elements\nNavigation: ${snapshot.navigation.map((n) => n.text).join(', ')}\nForms: ${snapshot.forms.length}\nTables: ${snapshot.tables.length}\n\nPages discovered so far: ${this.pagesDiscovered}\nUndiscovered nav links: ${this.getUndiscoveredLinks(snapshot)}`;

      const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/agent-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          message,
          currentSnapshot: snapshotSummary,
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        this.addAgentMessage(data.reply);
      }
    } catch {
      this.addAgentMessage("I captured this page but couldn't analyze it in detail. Please continue navigating.");
    }
  }

  private getUndiscoveredLinks(snapshot: PageSnapshot): string {
    const undiscovered = snapshot.navigation
      .filter((n) => {
        try {
          const path = new URL(n.href, location.origin).pathname;
          return !this.discoveredPaths.has(path);
        } catch {
          return false;
        }
      })
      .map((n) => n.text);

    return undiscovered.length > 0 ? undiscovered.join(', ') : 'None visible';
  }

  private async handleCustomerMessage(text: string): Promise<void> {
    this.addCustomerMessage(text);

    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/agent-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          message: text,
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        this.addAgentMessage(data.reply);
      }
    } catch {
      this.addAgentMessage("I couldn't process that. Please try again.");
    }
  }

  private async handleFinish(): Promise<void> {
    // Disable finish button to prevent double-clicks
    const finishBtn = this.shadow?.querySelector('.da-disc-finish') as HTMLButtonElement | null;
    if (finishBtn) {
      finishBtn.disabled = true;
      finishBtn.textContent = 'Generating...';
    }

    this.addSystemMessage('Finishing discovery and generating PDP configuration...\nThis may take up to a minute.');

    // Final click sync before finalize
    await this.syncClicks();

    try {
      // Summarize click sequences into workflow patterns
      const workflows = this.summarizeClickSequences();

      const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          clickSequences: workflows,
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        this.addAgentMessage(
          `Discovery complete! I've generated a PDP configuration with ${this.pagesDiscovered} screens and ${this.elementsDiscovered} elements.\n\nGo to your dashboard to review and publish the configuration.`,
        );

        // Remove token from URL
        const url = new URL(location.href);
        url.searchParams.delete('da_discover');
        history.replaceState(null, '', url.toString());
      } else {
        const errData = await resp.json().catch(() => ({ error: 'Unknown error' }));
        const detail = (errData.detail as string) ? `: ${errData.detail}` : '';
        this.addAgentMessage(
          `PDP generation failed${detail}\n\nYou can retry from the dashboard — all your discovered pages and notes have been saved.`,
        );
      }
    } catch {
      this.addAgentMessage('Failed to finalize. Please try again from the dashboard.');
    } finally {
      if (finishBtn) {
        finishBtn.disabled = false;
        finishBtn.textContent = 'Finish';
      }
    }
  }

  // ─── Scroll-Aware Capture ───────────────────────────────────────────

  private startScrollDetection(): void {
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        const anyNewVisible = entries.some((e) => e.isIntersecting);
        if (anyNewVisible) {
          // New content scrolled into view — schedule a content change check
          this.scheduleContentChangeCheck();
        }
      },
      { rootMargin: '200px', threshold: 0.1 },
    );

    // Observe sentinel elements near the bottom of scrollable areas
    this.refreshScrollSentinels();
  }

  /** Place sentinel observers on the last few items of lists and scrollable containers */
  private refreshScrollSentinels(): void {
    if (!this.scrollObserver) return;

    // Clean up old sentinels
    for (const el of this.observedSentinels) {
      this.scrollObserver.unobserve(el);
    }
    this.observedSentinels.clear();

    // Find scrollable containers and observe their last children
    const scrollables = document.querySelectorAll(
      '[style*="overflow"], [class*="scroll"], [class*="list"], [role="list"], [role="grid"], table tbody',
    );

    for (const container of scrollables) {
      const lastChild = container.lastElementChild;
      if (lastChild && !this.observedSentinels.has(lastChild)) {
        this.scrollObserver.observe(lastChild);
        this.observedSentinels.add(lastChild);
      }
    }
  }

  /** Summarize recorded clicks into per-page workflow sequences */
  private summarizeClickSequences(): Array<{ page: string; clicks: Array<{ label: string; tag: string }> }> {
    const byPage = new Map<string, Array<{ label: string; tag: string }>>();

    for (const click of this.clickSequence) {
      const existing = byPage.get(click.urlPath) ?? [];
      existing.push({ label: click.label, tag: click.tag });
      byPage.set(click.urlPath, existing);
    }

    return Array.from(byPage.entries()).map(([page, clicks]) => ({
      page,
      clicks: clicks.slice(0, 30), // Cap per page
    }));
  }

  // ─── Voice: STT (Browser) + TTS (ElevenLabs) ──────────────────────

  private initVoice(): void {
    if (!VOICE_ENABLED) return;
    // Enable TTS if server supports it
    if (this.config.voiceEnabled) {
      this.ttsEnabled = true;
      if (this.speakerBtnEl) {
        this.speakerBtnEl.style.display = '';
        this.speakerBtnEl.classList.remove('da-disc-speaker-off');
      }
    }

    // Check browser STT support
    const SpeechRecognitionCtor =
      (window as unknown as Record<string, unknown>).SpeechRecognition ??
      (window as unknown as Record<string, unknown>).webkitSpeechRecognition;

    if (SpeechRecognitionCtor) {
      this.voiceEnabled = true;
      this.recognition = new (SpeechRecognitionCtor as new () => SpeechRecognition)();
      this.recognition.continuous = false;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US';

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Show interim in input field
        if (interimTranscript && this.inputEl) {
          this.inputEl.value = interimTranscript;
        }

        // Send final transcript as message
        if (finalTranscript) {
          if (this.inputEl) this.inputEl.value = '';
          this.handleCustomerMessage(finalTranscript.trim());
        }
      };

      this.recognition.onend = () => {
        this.isListening = false;
        this.updateMicButton();
      };

      this.recognition.onerror = () => {
        this.isListening = false;
        this.updateMicButton();
      };
    }
  }

  private stopVoice(): void {
    if (this.isListening && this.recognition) {
      this.recognition.abort();
      this.isListening = false;
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
      this.isSpeaking = false;
    }
  }

  private toggleListening(): void {
    if (!this.recognition) return;

    if (this.isListening) {
      this.recognition.stop();
      this.isListening = false;
    } else {
      // Stop any playing audio first
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio = null;
        this.isSpeaking = false;
      }
      try {
        this.recognition.start();
        this.isListening = true;
        this.micToggled = true;
      } catch {
        // Already started or permission denied
        this.isListening = false;
      }
    }
    this.updateMicButton();
  }

  private updateMicButton(): void {
    if (!this.micBtnEl) return;
    this.micBtnEl.classList.toggle('da-disc-mic-active', this.isListening);
    this.micBtnEl.setAttribute('aria-label', this.isListening ? 'Stop listening' : 'Voice input');
  }

  private async speakResponse(text: string): Promise<void> {
    if (!this.ttsEnabled) return;

    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          text,
        }),
      });

      if (!resp.ok) return;

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      this.currentAudio = audio;
      this.isSpeaking = true;

      audio.onended = () => {
        URL.revokeObjectURL(url);
        this.isSpeaking = false;
        this.currentAudio = null;

        // Auto-listen after agent finishes speaking if mic is toggled on
        if (this.micToggled && this.recognition && !this.isListening) {
          try {
            this.recognition.start();
            this.isListening = true;
            this.updateMicButton();
          } catch {
            // Ignore
          }
        }
      };

      audio.onerror = () => {
        URL.revokeObjectURL(url);
        this.isSpeaking = false;
        this.currentAudio = null;
      };

      await audio.play().catch(() => {
        // Autoplay blocked — silently degrade to text-only
        this.isSpeaking = false;
        this.currentAudio = null;
      });
    } catch {
      // TTS failed — text-only fallback, non-fatal
    }
  }

  private toggleTTS(): void {
    this.ttsEnabled = !this.ttsEnabled;
    if (this.speakerBtnEl) {
      this.speakerBtnEl.classList.toggle('da-disc-speaker-off', !this.ttsEnabled);
      this.speakerBtnEl.setAttribute('aria-label', this.ttsEnabled ? 'Mute voice' : 'Unmute voice');
    }
    // Stop current audio if muting
    if (!this.ttsEnabled && this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
      this.isSpeaking = false;
    }
  }

  // ─── Click Sequence Sync ──────────────────────────────────────────

  private startClickSync(): void {
    this.clickSyncTimer = setInterval(() => this.syncClicks(), 30_000);

    this.beforeUnloadHandler = () => {
      this.syncClicksBeacon();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private stopClickSync(): void {
    if (this.clickSyncTimer) {
      clearInterval(this.clickSyncTimer);
      this.clickSyncTimer = null;
    }
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    // Final sync
    this.syncClicks();
  }

  private async syncClicks(): Promise<void> {
    if (this.clickSequence.length <= this.lastSyncedClickIndex) return;

    const delta = this.clickSequence.slice(this.lastSyncedClickIndex);
    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/sync-clicks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
          clicks: delta,
        }),
      });

      if (resp.ok) {
        this.lastSyncedClickIndex = this.clickSequence.length;
      }
    } catch {
      // Non-fatal — will retry next interval
    }
  }

  /** Use sendBeacon for reliable delivery on tab close */
  private syncClicksBeacon(): void {
    if (this.clickSequence.length <= this.lastSyncedClickIndex) return;

    const delta = this.clickSequence.slice(this.lastSyncedClickIndex);
    const body = JSON.stringify({
      sessionId: this.config.sessionId,
      token: this.config.token,
      clicks: delta,
    });

    navigator.sendBeacon(
      `${this.config.apiUrl}/api/v1/discover/sync-clicks`,
      new Blob([body], { type: 'application/json' }),
    );
    this.lastSyncedClickIndex = this.clickSequence.length;
  }

  // ─── UI Rendering ───────────────────────────────────────────────────

  private expanded = false;
  private unreadCount = 0;
  private badgeEl: HTMLSpanElement | null = null;

  private renderPanel(): void {
    this.panel = document.createElement('div');
    this.panel.id = 'da-discovery-panel';
    this.shadow = this.panel.attachShadow({ mode: 'closed' });

    const style = document.createElement('style');
    style.textContent = DISCOVERY_CSS;

    const container = document.createElement('div');
    container.className = 'da-disc';
    container.innerHTML = `
      <div class="da-disc-header">
        <span class="da-disc-dot"></span>
        <span class="da-disc-title">Discovery Agent</span>
        <div class="da-disc-coverage">
          <span class="da-disc-coverage-text">Pages: 0 | Elements: 0</span>
          <div class="da-disc-bar"><div class="da-disc-bar-fill" style="width: 0%"></div></div>
        </div>
        <span class="da-disc-badge" style="display:none">0</span>
        <button class="da-disc-speaker da-disc-speaker-off" aria-label="Unmute voice" style="display:none">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
        </button>
        <div class="da-disc-actions">
          <button class="da-disc-btn da-disc-finish">Finish</button>
          <button class="da-disc-btn da-disc-toggle" aria-label="Toggle panel">&#x25B2;</button>
        </div>
      </div>
      <div class="da-disc-body">
        <div class="da-disc-messages"></div>
        <div class="da-disc-input-area">
          <textarea class="da-disc-input" placeholder="Type a note or answer..." rows="1"></textarea>
          <button class="da-disc-mic" aria-label="Voice input" style="display:none">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          </button>
          <button class="da-disc-send">Send</button>
        </div>
      </div>
    `;

    this.shadow.appendChild(style);
    this.shadow.appendChild(container);
    document.body.appendChild(this.panel);

    // Cache refs
    this.messagesEl = container.querySelector('.da-disc-messages')!;
    this.coverageEl = container.querySelector('.da-disc-coverage-text')!;
    this.inputEl = container.querySelector('.da-disc-input')!;
    this.badgeEl = container.querySelector('.da-disc-badge')!;
    this.micBtnEl = container.querySelector('.da-disc-mic')!;
    this.speakerBtnEl = container.querySelector('.da-disc-speaker')!;

    const toggleBtn = container.querySelector('.da-disc-toggle')!;

    // Show voice buttons if supported
    if (this.voiceEnabled && this.micBtnEl) {
      this.micBtnEl.style.display = '';
    }

    // Events
    container.querySelector('.da-disc-header')!.addEventListener('click', (e: Event) => {
      // Don't toggle when clicking buttons inside the header
      if ((e.target as HTMLElement).closest('.da-disc-finish, .da-disc-send, .da-disc-speaker')) return;
      this.togglePanel(container, toggleBtn as HTMLElement);
    });
    container.querySelector('.da-disc-finish')!.addEventListener('click', () => this.handleFinish());
    container.querySelector('.da-disc-send')!.addEventListener('click', () => {
      const text = this.inputEl!.value.trim();
      if (text) {
        this.handleCustomerMessage(text);
        this.inputEl!.value = '';
      }
    });
    this.micBtnEl?.addEventListener('click', () => this.toggleListening());
    this.speakerBtnEl?.addEventListener('click', () => this.toggleTTS());
    this.inputEl!.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const text = this.inputEl!.value.trim();
        if (text) {
          this.handleCustomerMessage(text);
          this.inputEl!.value = '';
        }
      }
    });
  }

  private togglePanel(container: HTMLElement, toggleBtn: HTMLElement): void {
    this.expanded = !this.expanded;
    container.classList.toggle('da-disc-expanded', this.expanded);
    toggleBtn.innerHTML = this.expanded ? '&#x25BC;' : '&#x25B2;';
    if (this.expanded) {
      this.unreadCount = 0;
      if (this.badgeEl) this.badgeEl.style.display = 'none';
      this.messagesEl!.scrollTop = this.messagesEl!.scrollHeight;
    }
  }

  private addAgentMessage(text: string): void {
    this.addMessage('agent', text);
    this.speakResponse(text);
  }

  private addCustomerMessage(text: string): void {
    this.addMessage('customer', text);
  }

  private addSystemMessage(text: string): void {
    this.addMessage('system', text);
  }

  private addMessage(role: 'agent' | 'customer' | 'system', text: string): void {
    if (!this.messagesEl) return;

    const div = document.createElement('div');
    div.className = `da-disc-msg da-disc-msg-${role}`;
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;

    // Track unread when collapsed
    if (!this.expanded && role !== 'customer') {
      this.unreadCount++;
      if (this.badgeEl) {
        this.badgeEl.textContent = String(this.unreadCount);
        this.badgeEl.style.display = '';
      }
    }
  }

  private updateCoverage(): void {
    if (!this.coverageEl) return;
    this.coverageEl.textContent = `Pages: ${this.pagesDiscovered} | Elements: ${this.elementsDiscovered}`;

    // Update progress bar (estimate based on nav links)
    const barFill = this.shadow?.querySelector('.da-disc-bar-fill') as HTMLElement;
    if (barFill) {
      // Rough estimate: most products have 5-15 main screens
      const estimated = Math.max(8, this.discoveredPaths.size + 3);
      const pct = Math.min(100, Math.round((this.pagesDiscovered / estimated) * 100));
      barFill.style.width = `${pct}%`;
    }
  }
}

const DISCOVERY_CSS = `
  :host { all: initial; }

  .da-disc {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: #0a0a0a;
    border-top: 1px solid #262626;
    color: #fafafa;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    z-index: 2147483647;
    box-shadow: 0 -4px 20px rgba(0,0,0,0.3);
    transition: height 0.25s ease;
    height: 44px;
    overflow: hidden;
  }

  .da-disc.da-disc-expanded {
    height: min(400px, 50vh);
  }

  .da-disc-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    cursor: pointer;
    user-select: none;
    flex-shrink: 0;
    min-height: 44px;
    box-sizing: border-box;
  }

  .da-disc-header:hover {
    background: #141414;
  }

  .da-disc-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ef4444;
    animation: pulse 2s infinite;
    flex-shrink: 0;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .da-disc-title {
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .da-disc-coverage {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .da-disc-coverage-text {
    font-size: 11px;
    color: #a3a3a3;
    white-space: nowrap;
  }

  .da-disc-bar {
    height: 3px;
    background: #262626;
    border-radius: 2px;
    width: 80px;
    flex-shrink: 0;
  }

  .da-disc-bar-fill {
    height: 100%;
    background: #7c3aed;
    border-radius: 2px;
    transition: width 0.5s ease;
  }

  .da-disc-badge {
    background: #ef4444;
    color: white;
    font-size: 10px;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    line-height: 18px;
    text-align: center;
    border-radius: 9px;
    padding: 0 5px;
    flex-shrink: 0;
  }

  .da-disc-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }

  .da-disc-btn {
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid #7c3aed;
    background: transparent;
    color: #7c3aed;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    line-height: 1.4;
  }

  .da-disc-btn:hover {
    background: #7c3aed;
    color: white;
  }

  .da-disc-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: transparent;
    color: #7c3aed;
  }

  .da-disc-toggle {
    border-color: #404040 !important;
    color: #a3a3a3 !important;
    font-size: 10px !important;
    padding: 4px 8px !important;
  }

  .da-disc-toggle:hover {
    background: #262626 !important;
    color: #fafafa !important;
  }

  .da-disc-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    border-top: 1px solid #262626;
  }

  .da-disc-messages {
    flex: 1;
    overflow-y: auto;
    padding: 10px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .da-disc-msg {
    padding: 6px 10px;
    border-radius: 8px;
    line-height: 1.4;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .da-disc-msg-agent {
    background: #1e1e2e;
    border-bottom-left-radius: 2px;
  }

  .da-disc-msg-customer {
    background: #7c3aed;
    color: white;
    align-self: flex-end;
    max-width: 85%;
    border-bottom-right-radius: 2px;
  }

  .da-disc-msg-system {
    font-size: 11px;
    color: #a3a3a3;
    font-style: italic;
    padding: 2px 0;
  }

  .da-disc-input-area {
    display: flex;
    gap: 8px;
    padding: 8px 16px;
    border-top: 1px solid #262626;
    flex-shrink: 0;
  }

  .da-disc-input {
    flex: 1;
    background: #1a1a2e;
    border: 1px solid #262626;
    border-radius: 6px;
    padding: 6px 10px;
    color: #fafafa;
    font-size: 13px;
    font-family: inherit;
    resize: none;
    outline: none;
  }

  .da-disc-input:focus {
    border-color: #7c3aed;
  }

  .da-disc-send {
    padding: 6px 14px;
    background: #7c3aed;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
  }

  .da-disc-send:hover {
    background: #6d28d9;
  }

  .da-disc-mic {
    width: 34px;
    height: 34px;
    background: transparent;
    border: 1px solid #404040;
    border-radius: 6px;
    color: #a3a3a3;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }

  .da-disc-mic:hover {
    border-color: #7c3aed;
    color: #7c3aed;
  }

  .da-disc-mic-active {
    background: #ef4444 !important;
    border-color: #ef4444 !important;
    color: white !important;
    animation: pulse 1.5s infinite;
  }

  .da-disc-speaker {
    width: 28px;
    height: 28px;
    background: transparent;
    border: 1px solid #404040;
    border-radius: 6px;
    color: #a3a3a3;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }

  .da-disc-speaker:hover {
    border-color: #7c3aed;
    color: #7c3aed;
  }

  .da-disc-speaker-off {
    opacity: 0.4;
  }

  .da-disc-messages::-webkit-scrollbar { width: 4px; }
  .da-disc-messages::-webkit-scrollbar-thumb { background: #262626; border-radius: 2px; }
`;
